import _ from "lodash";
import { useMutation, useQueryClient } from "react-query"
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { setIsAuthenticated, setUserInfo } from "../redux/Slices/Global/globalSlice";
import { emptyLocalStorage, setProfile } from "../config/global";
import { useCustomToast } from "../pages/Contexts/ToastContext";
import { handleResErrors } from "./utils";
import { isVerifiedValue } from "../utils/authSession";
import axiosInstance from "../config/Axios"

// ── Inline vs toast ─────────────────────────────────────────────────────────
// Every hook below takes `{ inline: true }`. The customer auth pages pass it:
// they render their own banners from the rejected mutateAsync() promise (see
// components/auth/authErrors.js), so the hook must NOT also pop a bottom toast.
// Without the option a hook behaves exactly as before (toast + redirects).

// Login
const login = async (payload) => {
    const { data } = await axiosInstance.post("/users/login", payload);

    return data.data;
}

export const useLogInMutation = ({ inline = false } = {}) => {
    const { showToast } = useCustomToast();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    return useMutation(login, {
        onSuccess: async (data) => {
            if (data?.type === "USER" || data?.type === "OWNER") {
                if (!isVerifiedValue(data?.email_verified_at)) {
                    localStorage.setItem("email", data?.email);
                    // Never store the string "null" here: it is truthy, and the
                    // route guards read this key to decide "signed in".
                    localStorage.removeItem("email_verified_at");
                    localStorage.setItem("verification_attempts", data?.verification_attempts ?? 0);
                    navigate("/verify/email");
                }
                else {
                    dispatch(setIsAuthenticated(true))
                    setProfile(data);
                    dispatch(setUserInfo(data))
                    // inline: the login page shows its "opening your dashboard…"
                    // state first and navigates itself a moment later.
                    if (!inline) navigate("/dashboard");
                }
            }
        },
        onError: (error) => { if (!inline) showToast("error", 'Login Credential', error.response?.data?.message) }
    })
}

// Registration
const register = async (payload) => {
    const { data } = await axiosInstance.post("/users/signup", payload);

    return data.data;
}

export const useRegisterMutation = ({ inline = false } = {}) => {
    const { showToast } = useCustomToast();
    const navigate = useNavigate();

    return useMutation(register, {
        onSuccess: async (data) => {
            localStorage.setItem("email", data?.email);
            navigate("/verify/email")
        },
        onError: (error) => { if (!inline) handleResErrors(error, showToast) }
    })
}

// Employee Registration
const employeeRegister = async (payload) => {
    const { data } = await axiosInstance.post(`/users/accept/${payload?.invitationId}/invitation`, payload?.data);

    return data.data;
}

export const useEmployeeRegisterMutation = ({ inline = false } = {}) => {
    const { showToast } = useCustomToast();
    const dispatch = useDispatch()
    const navigate = useNavigate();

    return useMutation(employeeRegister, {
        onSuccess: async (data) => {
            setProfile(data);
            dispatch(setUserInfo(data))
            navigate("/dashboard");
        },
        onError: (error) => { if (!inline) handleResErrors(error, showToast) }
    })
}

// Verify Email
const verifyEmail = async (payload) => {
    const { data } = await axiosInstance.post("/users/verify/email", payload);

    // `already_verified` is a sibling of `data` in the response: true when the link was
    // already used (the server then issues NO session).
    return { ...data.data, already_verified: !!data.already_verified };
}

export const useVerifyEmailMutation = ({ inline = false } = {}) => {
    const { showToast } = useCustomToast();
    const navigate = useNavigate();

    return useMutation(verifyEmail, {
        onSuccess: async (data) => {
            // Used link: no session exists. Do not store a profile or navigate; the confirm page
            // shows its "already active, sign in" view.
            if (data?.already_verified) return;
            // The endpoint answers { status, msg, data: <user> }; the user is one
            // level down. Passing the wrapper stored name/email/email_verified_at
            // as the string "undefined" (which only "worked" because that string
            // is truthy).
            setProfile(data?.data ?? data)
            if (inline) return; // the confirm page shows its success state, then navigates itself
            showToast('success', 'Email Verification', "Email has been verified successfully!");
            navigate("/dashboard")
        },
        onError: (error) => {
            // inline: the confirm page shows its own "couldn't activate" state
            // and stays where it is.
            if (inline) return;
            navigate('/login')
            showToast("error", "Email Verification", error?.response?.data?.message)
        }
    })
}

// Resend Verification Email
const resendEmail = async (payload) => {
    const { data } = await axiosInstance.post("users/resend/email/verification", payload);

    return data.data;
}

export const useResendEmailMutation = ({ inline = false } = {}) => {
    const { showToast } = useCustomToast();

    return useMutation(resendEmail, {
        onSuccess: async (data) => {
            localStorage.setItem("verification_attempts", data?.verification_attempts);
            if (!inline) showToast("success", "Email Verification", data?.msg);
        },
        onError: (error) => {
            let _data = error?.response?.data;
            // Only a real counter: a network failure / 429 has no body, and
            // writing undefined stored the string "undefined".
            if (_data?.verification_attempts != null) {
                localStorage.setItem("verification_attempts", _data.verification_attempts);
            }
            if (!inline) showToast("error", "Email Verification", _data?.msg)
        }
    })
}

// Forget Password
const forgetPassword = async (payload) => {
    const { data } = await axiosInstance.post("/users/forget-password", payload);

    return data.data;
}

export const useForgetPasswordMutation = ({ inline = false } = {}) => {
    const { showToast } = useCustomToast();

    return useMutation(forgetPassword, {
        onSuccess: async (data) => {
            if (!inline) showToast('success', 'Forget Password', data?.msg);
        },
        onError: (error) => {
            if (!inline) showToast('error', 'Forget Password', error?.response?.data?.message);
        }
    })
}

// Reset Password
const resetPassword = async (payload) => {
    const { data } = await axiosInstance.post(`/users/reset-password/${payload?.token}`, payload.data)

    return data.data;
}

export const useResetPasswordMutation = ({ inline = false } = {}) => {
    const { showToast } = useCustomToast();
    const navigate = useNavigate();

    return useMutation(resetPassword, {
        onSuccess: async (data) => {
            if (!inline) showToast('success', 'Reset Password', data?.msg);
            // The login page turns this into the "password changed" banner.
            navigate("/login?reason=password_changed");
        },
        onError: (error) => {
            if (!inline) showToast('error', 'Reset Password', error.response?.data?.message);
        }
    })
}

// Logout
const logout = async () => {
    const { data } = await axiosInstance.get(`/users/logout`)
    return data.data;
}

export const useLogoutMutation = () => {
    const { showToast } = useCustomToast();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    return useMutation(logout, {
        onSuccess: async (data) => {
            queryClient.clear();
            showToast('success', 'Logout', data?.msg);
            emptyLocalStorage()
            navigate("/login");
        },
        onError: (error) => {
            showToast('error', 'Logout', error.response?.data?.message);
        }
    })
}